import _ from "lodash";
import utils from "./firebaseUtils";
import {
  FIREBASE_COLLECTION_GAMES,
  FIREBASE_GAME_FIELD_ROOMID,
  FIREBASE_GAME_FIELD_MANAGERID,
  EQUALTO,
  FIREBASE_FUNCTION_NAME_RESET_GAME,
  FIREBASE_FUNCTION_COPY_GAME,
  FIREBASE_FUNCTION_CREATE_GAME,
  FIREBASE_FUNCTION_CREATE_GAME_V2,
  FIREBASE_FUNCTION_DELETE_GAME,
  FIREBASE_FUNCTION_DEBUG_SET_PRE_FINAL,
  FIREBASE_FUNCTION_RETRY_GAME_GENERATION,
} from "./constants";
import {
  loadModelForDocumentSnapshot,
  loadModelsForQuerySnapshot,
  loadDiffOfModelsForQuerySnapshot,
} from "./firebaseModels";
import { computeDiff } from "../../utils/diffUtils";
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp
} from "firebase/firestore";
import { PUBLISH_STATUS } from "features/manager/lifecycle";
import { ensureFirebaseAuth } from "../auth/ensureFirebaseAuth";

import logging from "utils/logging";

const log = logging.getLogger("firebase/game");
log.setLevel('info');

const createFullGame = async ({ gameName, roundCount, categoryCount, questionCount, description, managerId, useLorem=false }) => {
  // Call the cloud function to do this. Get the new game ID back and return it.
  log.info("Creating full game: ", { gameName, roundCount, categoryCount, questionCount, description, managerId, useLorem });
  const createGameFunc = utils.getCallableFunction(FIREBASE_FUNCTION_CREATE_GAME);
  const result = await createGameFunc({
    name: gameName,
    roundCount,
    categoryCount,
    questionCount,
    description,
    managerId,
    useLorem,
  });
  log.info(`Created game called "${gameName}" with result:`, result);
  return result.data.gameId;
};

const createGameWithAI = async (formValues) => {
  log.info("Creating AI-generated game: ", formValues);
  await ensureFirebaseAuth();
  const createGameV2Func = utils.getCallableFunction(FIREBASE_FUNCTION_CREATE_GAME_V2);

  try {
    const result = await createGameV2Func({
      name: formValues.name,
      theme: formValues.theme || 'General Knowledge',
      difficulty: formValues.difficulty,
      tone: formValues.tone,
      host: formValues.host,
      customCategories: formValues.customCategories || [],
      rounds: formValues.rounds,
      categoriesPerRound: formValues.categoriesPerRound,
      questionsPerCategory: formValues.questionsPerCategory,
      uid: formValues.uid,
    });

    log.info(`Created AI game with result:`, result);
    return result.data;
  } catch (error) {
    log.error("Error creating AI game:", error);
    throw error;
  }
};

const retryGameGeneration = async (gameId) => {
  const callable = utils.getCallableFunction(FIREBASE_FUNCTION_RETRY_GAME_GENERATION);
  const result = await callable({ gameId });
  return result.data;
};

const resetGame = async (gameId) => {
  const resetGameFunc = utils.getCallableFunction(FIREBASE_FUNCTION_NAME_RESET_GAME);
  const result = await resetGameFunc({ gameId });
  log.info("Got result from resetting game: ", result);
  return result;
};

const copyGame = async (gameId, newGameName) => {
  const copyGameFunc = utils.getCallableFunction(FIREBASE_FUNCTION_COPY_GAME);
  const result = await copyGameFunc({ gameId, name: newGameName });
  log.info("Copied game: ", result);
  return result;
};

const deleteGame = async (gameId) => {
  const deleteGameFunc = utils.getCallableFunction(FIREBASE_FUNCTION_DELETE_GAME);
  const result = await deleteGameFunc({ gameId });
  log.info("Deleted game result: ", result);
  return result;
};

const getGameForRoomId = async (roomId) => {
  const db = utils.db();
  const gamesCollection = collection(db, FIREBASE_COLLECTION_GAMES);
  const roomQuery = query(
    gamesCollection,
    where(FIREBASE_GAME_FIELD_ROOMID, "==", roomId)
  );

  const querySnap = await getDocs(roomQuery);

  const rooms = await loadModelsForQuerySnapshot(querySnap);
  if (rooms.length === 0) {
    return null;
  } else if (rooms.length === 1) {
    return rooms[0];
  } else {
    log.error("Found multiple rooms with the same roomId");
    log.info("Games: ", rooms);
    return null;
  }
};

const watchGame = async (gameId, onChange) => {
  const db = utils.db();
  const gameRef = doc(db, FIREBASE_COLLECTION_GAMES, gameId);

  let lastGame = null;

  return onSnapshot(gameRef, async (gameSnap) => {
    log.debug("Got a game snapshot: ", gameSnap.data());

    let game = await loadModelForDocumentSnapshot(gameSnap);

    if (lastGame !== null && _.isEqual(lastGame, game)) {
      log.debug("No changes to game: ", game);
      return;
    } else {
      log.info("Game changed: ", computeDiff(lastGame, game));
      lastGame = game;
    }
    
    log.debug("Updated data: ", game);
    onChange(game);
  });
};

const watchManagedGames = async (userId, onChange) => {
  log.debug("Setting up watchManagedGames for userId:", userId);
  
  const db = utils.db();
  const gamesCollection = collection(db, FIREBASE_COLLECTION_GAMES);
  const gameQuery = query(
    gamesCollection,
    where(FIREBASE_GAME_FIELD_MANAGERID, EQUALTO, userId)
  );

  log.debug("Game query created with:", {
    collection: FIREBASE_COLLECTION_GAMES,
    field: FIREBASE_GAME_FIELD_MANAGERID,
    value: userId
  });

  return onSnapshot(gameQuery, async (gameQuerySnap) => {
    log.debug("Received snapshot with:", {
      changes: gameQuerySnap.docChanges().length,
      empty: gameQuerySnap.empty,
      fromCache: gameQuerySnap.metadata.fromCache,
      hasPendingWrites: gameQuerySnap.metadata.hasPendingWrites
    });

    if (gameQuerySnap.empty) {
      log.debug("No games found for managerId:", userId);
      onChange({ added: [], deleted: [], updated: [] });
      return;
    }

    let diff = await loadDiffOfModelsForQuerySnapshot(gameQuerySnap);
    log.debug("Got diffed games: ", diff);
    onChange(diff);
  }, (error) => {
    log.error("Error in watchManagedGames:", error);
    if (error.code === 'permission-denied') {
      log.error("Permission denied - check Firebase security rules");
    }
  });
};

const watchGameForRoomId = async (roomId, onChange) => {
  let game = await getGameForRoomId(roomId);

  if (game === null) {
    return null;
  }

  const db = utils.db();
  const gameRef = doc(db, FIREBASE_COLLECTION_GAMES, game.id);

  return onSnapshot(gameRef, async (gameSnap) => {
    log.debug("Got a game snapshot for room: ", gameSnap.data());
    let game = await loadModelForDocumentSnapshot(gameSnap);
    onChange(game);
  });
};

const updateGameState = async (gameId, newState) => {
  await ensureFirebaseAuth();
  const db = utils.db();
  const gameRef = doc(db, FIREBASE_COLLECTION_GAMES, gameId);

  await updateDoc(gameRef, { state: newState });
};

const updateGameSettings = async (gameId, settings) => {
  await ensureFirebaseAuth();
  const db = utils.db();
  const gameRef = doc(db, FIREBASE_COLLECTION_GAMES, gameId);

  await updateDoc(gameRef, { settings });
};

const watchGameGeneration = (gameId, onChange) => {
  log.debug("Watching game generation for game: ", gameId);

  const db = utils.db();

  // Watch the progress subcollection for detailed progress updates
  const progressRef = doc(db, FIREBASE_COLLECTION_GAMES, gameId, 'generation', 'progress');

  // Also watch main game doc for status
  const gameRef = doc(db, FIREBASE_COLLECTION_GAMES, gameId);

  // Keep track of latest data from both sources
  let progressData = null;
  let gameData = null;

  const mergeAndNotify = () => {
    if (progressData || gameData) {
      // Merge progress data with game data
      const failedAtRaw = gameData?.generation?.failedAt;
      const failedAtIso = failedAtRaw?.toDate
        ? failedAtRaw.toDate().toISOString()
        : (typeof failedAtRaw === 'string' ? failedAtRaw : null);

      const merged = {
        overallProgress: progressData?.overallProgress || 0,
        currentPhase: gameData?.generation?.currentPhase || gameData?.generation?.status || 'initializing',
        status: gameData?.generation?.status || 'initializing',
        estimatedTimeRemaining: progressData?.estimatedTimeRemaining || 300,
        currentStep: progressData?.currentStep || 'Initializing...',
        categories: progressData?.categories || [],
        categoryCount: progressData?.categoriesList?.length || 0,
        startedAt: progressData?.startedAt || null,
        roundCount: progressData?.roundCount || 1,
        categoryNames: progressData?.categoryNames || [],
        name: gameData?.name || null,
        // Failure metadata — surfaced to ErrorState + WhatWentWrong
        lastCompletedPhase: gameData?.generation?.lastCompletedPhase || null,
        errorMessage: gameData?.generation?.errorMessage || null,
        failedAt: failedAtIso,
        retriedAt: gameData?.generation?.retriedAt || null,
        questionCount: (() => {
          const ctx = gameData?.context;
          if (!ctx) return 0;
          const rounds = ctx.roundCount || ctx.rounds || 1;
          const catsPerRound = ctx.categoryCount || ctx.categoriesPerRound || 0;
          const qPerCat = ctx.questionCount || ctx.questionsPerCategory || 5;
          return rounds * catsPerRound * qPerCat;
        })(),
        hasFinale: true,
      };
      onChange(merged);
    }
  };

  // Watch progress subcollection
  const progressUnsubscribe = onSnapshot(
    progressRef,
    (progressSnap) => {
      if (progressSnap.exists()) {
        progressData = progressSnap.data();
        log.debug("Progress updated:", progressData);
        mergeAndNotify();
      }
    },
    (err) => {
      log.error("watchGameGeneration progress listener failed", err);
      onChange({ status: 'failed', errorMessage: err.message, listenerError: true });
    }
  );

  // Watch main game document for status
  const gameUnsubscribe = onSnapshot(
    gameRef,
    async (gameSnap) => {
      const game = await loadModelForDocumentSnapshot(gameSnap);
      gameData = game;
      log.debug("Game status updated:", game?.generation);
      mergeAndNotify();
    },
    (err) => {
      log.error("watchGameGeneration game listener failed", err);
      onChange({ status: 'failed', errorMessage: err.message, listenerError: true });
    }
  );

  // Return a function that unsubscribes from both listeners
  return () => {
    progressUnsubscribe();
    gameUnsubscribe();
  };
};

/* ── Lifecycle status transitions (dashboard "My Games") ──────────────────
 * Client-side game-doc writes. Each transition bumps lastTouchedAt so the
 * dashboard re-sorts, and stamps statusChangedAt for hero tiebreaks. See
 * docs/specs/2026-05-28-manager-dashboard-design.md §3.2 / §11.
 */
const gameDocRef = (gameId) => doc(utils.db(), FIREBASE_COLLECTION_GAMES, gameId);

// Shared write path for the direct (non-callable) lifecycle transitions. Like
// the callable paths, it ensures the Clerk→Firebase session is established
// first (the bridge can fail silently), and it logs + rethrows so the caller
// can surface an actionable error toast rather than silently appearing to
// succeed while the write was rejected.
const writeLifecycle = async (gameId, fields, opName) => {
  await ensureFirebaseAuth();
  try {
    await updateDoc(gameDocRef(gameId), fields);
  } catch (error) {
    log.error(`${opName} failed`, { gameId, error });
    throw error;
  }
};

// draft → ready. Caller gates on 100% completion before invoking.
const markGameReady = (gameId) =>
  writeLifecycle(
    gameId,
    {
      publishStatus: PUBLISH_STATUS.READY,
      statusChangedAt: serverTimestamp(),
      lastTouchedAt: serverTimestamp(),
    },
    "markGameReady"
  );

// ready → draft (manual escape hatch for revisions).
const moveGameToDraft = (gameId) =>
  writeLifecycle(
    gameId,
    {
      publishStatus: PUBLISH_STATUS.DRAFT,
      statusChangedAt: serverTimestamp(),
      lastTouchedAt: serverTimestamp(),
    },
    "moveGameToDraft"
  );

// any non-archived → archived. Hides from default views.
const archiveGame = (gameId) =>
  writeLifecycle(
    gameId,
    {
      publishStatus: PUBLISH_STATUS.ARCHIVED,
      archivedAt: serverTimestamp(),
      statusChangedAt: serverTimestamp(),
      lastTouchedAt: serverTimestamp(),
    },
    "archiveGame"
  );

// archived → ready (if complete) else draft. §15 default.
//
// We can't recover the pre-archive lifecycle state (in-progress? played?), and
// landing a previously-played/in-progress game directly on "ready" would be a
// lie — its runtime state, scores, and submissions would still be populated.
// resetGame owns the "back to a clean, launchable state" transition: it clears
// runtime + submissions, recomputes completion, sets publishStatus to ready
// (if 100%) or draft, and clears archivedAt — all atomically. So unarchive is
// just a reset. Resetting a never-launched draft is a harmless no-op.
const unarchiveGame = async (gameId) => {
  await resetGame(gameId);
};

// in-progress → played ("Mark complete" manual path). Preserve first-played.
const markGamePlayed = (gameId, { alreadyPlayed } = {}) =>
  writeLifecycle(
    gameId,
    {
      publishStatus: PUBLISH_STATUS.PLAYED,
      ...(alreadyPlayed ? {} : { playedAt: serverTimestamp() }),
      statusChangedAt: serverTimestamp(),
      lastTouchedAt: serverTimestamp(),
    },
    "markGamePlayed"
  );

// played/in-progress → ready ("Replay" / "Reset session"). resetGame clears
// runtime + submissions and flips publishStatus back to ready (or draft if the
// game is no longer complete), preserving playedAt.
const replayGame = async (gameId) => {
  await resetGame(gameId);
};

// Manual status override (header / hero status menu). Jumps publishStatus to an
// explicit target, bypassing the usual state-driven transitions — so a host can
// e.g. set "in-progress" without the game actually being in lobby. Stamps the
// relevant side-effect timestamps so the override stays internally consistent
// with the dedicated transitions above.
const setGameStatus = (gameId, status) => {
  const update = {
    publishStatus: status,
    statusChangedAt: serverTimestamp(),
    lastTouchedAt: serverTimestamp(),
  };
  if (status === PUBLISH_STATUS.PLAYED) update.playedAt = serverTimestamp();
  if (status === PUBLISH_STATUS.ARCHIVED) update.archivedAt = serverTimestamp();
  return writeLifecycle(gameId, update, "setGameStatus");
};

const debugSetPreFinal = async (gameId) => {
  const fn = utils.getCallableFunction(FIREBASE_FUNCTION_DEBUG_SET_PRE_FINAL);
  const result = await fn({ gameId });
  log.info("Got result from debugSetPreFinal: ", result);
  return result;
};

const api = {
  resetGame,
  retryGameGeneration,
  copyGame,
  deleteGame,
  markGameReady,
  moveGameToDraft,
  archiveGame,
  unarchiveGame,
  markGamePlayed,
  replayGame,
  setGameStatus,
  debugSetPreFinal,
  getRoom: getGameForRoomId,
  watchGame,
  watchManagedGames,
  watchGameForRoomId,
  updateGameState,
  updateGameSettings,
  createGame: createFullGame,
  createGameWithAI,
  watchGameGeneration,
};

export default api;
